import {ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, ViewChild, ElementRef, AfterViewInit} from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { PermissionsService } from 'src/app/services/permissions/permissions.service';
import {AlertBannerService, AlertType} from "../../lib/alert-banner";
import {OrganisationService} from "../../services/organisation/organisation.service";
import {TrustMarketplaceStatusService} from "../../services/trust-marketplace-status/trust-marketplace-status.service";
import {ConstantsService} from "../../services/constants/constants.service";
import {debounceTime, distinctUntilChanged, switchMap, takeUntil} from "rxjs/operators";
import moment from "moment";
import {RequestTemplateService} from "../../services/request-template/request-template.service";
import {Subject, of, fromEvent} from "rxjs";
import {Router} from "@angular/router";

@Component({
  selector: 'app-select-check-type-modal',
  templateUrl: './select-check-type-modal.component.pug',
  styleUrls: ['./select-check-type-modal.component.scss'],
  standalone: false
})
export class SelectCheckTypeModalComponent implements OnInit, OnDestroy {
  protected readonly ConstantsService = ConstantsService;
  public selectClick: EventEmitter<any> = new EventEmitter();
  hasBackgroundCheckPermissions = false;
  tmpAdminStatus: number | undefined = 1;

  canSendReferenceRequest = false;
  canSendPulseRequest = false;
  canSendExitRequest = false;
  canSendBackgroundRequest = false;
  requestTemplate = false;
  canManageTemplate = false;
  templates:any[] = [];
  loading = false;
  templatesLoaded = false;
  currentPage = 1;
  rowsPerView = 20;
  private destroy$ = new Subject<void>();
  private searchSubject$ = new Subject<string>();
  total = 0;
  searchTitle = $localize`Packages will appear here.`;
  searchDescription = $localize`Packages save time by providing consistent, predefined settings that instantly apply to new requests. Visibility is based on product access and survey permission.`;
  createBtn = $localize`Create a package`
  searchString = '';
  noSearchTitle = $localize`Sorry, we couldn’t find any results`
  noSearchDescription = $localize`Try another search term or adjust the filters.`
  searchImg = "https://assets.xref.com/omni/shared/tmp_console_no_vendors.svg"
  referenceRequestTooltip = '';
  helpToolTip = $localize`Start your request using a package containing pre-configured settings. `;
  pulseRequestTooltip = '';
  exitRequestTooltip = '';
  backgroundRequestTooltip = '';

  constructor(
    public router: Router,
    private organisationService: OrganisationService,
    private alertList: AlertBannerService,
    public bsModalRef: BsModalRef,
    private trustMarketplaceStatusService: TrustMarketplaceStatusService,
    private permissions: PermissionsService,
    private templateService: RequestTemplateService,
    private cdr: ChangeDetectorRef,

  ) { }

  ngOnInit(): void {
    this.requestTemplate = this.permissions.isFeatureActivated('ent-10605-request-templates');
    this.fetchTrustMarketplaceStatus()
    this.canSendReferenceRequest = this.permissions.hasPermission('can_send_reference_requests') || this.permissions.hasPermission('can_send_reference_requests_with_survey_perms')
    this.canSendPulseRequest = this.permissions.hasPermission('can_send_pulse_requests') || this.permissions.hasPermission('can_send_pulse_requests_with_survey_perms')
    this.canSendExitRequest = this.permissions.hasPermission('can_send_exit_requests') || this.permissions.hasPermission('can_send_exit_requests_with_survey_perms')
    this.canSendBackgroundRequest = this.permissions.hasPermission('can_send_backgroundcheck_requests')

    this.referenceRequestTooltip = !this.canSendReferenceRequest ? ConstantsService.REFERENCE_NONE_TOOLTIP : '';
    this.pulseRequestTooltip = !this.canSendPulseRequest ? ConstantsService.PULSE_CHECK_NONE_TOOLTIP : '';
    this.exitRequestTooltip = !this.canSendExitRequest ? ConstantsService.EXIT_CHECK_NONE_TOOLTIP : '';
    this.backgroundRequestTooltip = !this.hasBackgroundCheckPermissions || !this.canSendBackgroundRequest  ? ConstantsService.BACKGROUND_CHECK_NONE_TOOLTIP : '';
    this.canManageTemplate = this.permissions.hasPermission("can_manage_package") ||
        this.permissions.hasPermission("can_manage_reference_package") ||
        this.permissions.hasPermission("can_manage_backgroundcheck_package")
    if(this.requestTemplate){
        this.setupSearchSubscription();
        this.getAndProcessTemplatesList();
    }

  }

  private setupSearchSubscription(): void {
    this.searchSubject$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe((searchTerm: string) => {
      this.searchString = searchTerm;
      this.currentPage = 1; // Reset to first page on new search
      this.getAndProcessTemplatesList();
    });
  }
  getAndProcessTemplatesList(): void {
    this.loading = true;
    this.templatesLoaded = false;

    this.templateService.listTemplates(this.rowsPerView, this.currentPage, this.searchString, '-last_used', 'active').pipe(
        takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        // Use requestAnimationFrame to ensure update happens after current frame
        // This avoids ExpressionChangedAfterItHasBeenCheckedError
        requestAnimationFrame(() => {
          this.processTemplates(response['templates'] || response['data'] || []);
          this.total = response['total'] || response['count'] || 0;
          this.templatesLoaded = true;
          this.loading = false;
          this.cdr.detectChanges();
        });
      },
      error: (error) => {
        console.error('Error loading templates:', error);
        this.alertList.append([{
          type: AlertType.DANGER,
          message: $localize`Unable to load templates. Please try again.`,
          keepOnRouteChange: false
        }]);
        requestAnimationFrame(() => {
          this.templatesLoaded = true;
          this.loading = false;
          this.cdr.detectChanges();
        });
      },
      complete: () => {
        // Complete handler - state already updated in next/error handlers
      }
    });
  }

  private processTemplates(templates: any[]): void {
    // Process templates: extract data, format dates, set kebab options
    this.templates = templates.map(template => {
      // Find the background config block
      const backgroundConfig = template.config?.find(c => c.type === 'background');

      // Extract partner check names from partner_checks array
      const backgroundChecksArray = backgroundConfig?.partner_checks || [];
      const backgroundChecks = backgroundChecksArray.length > 0
        ? backgroundChecksArray.map((check: any) => check.name).join(', ')
        : null;

      // Find the reference config block
      const referenceConfig = template.config?.find(c => c.type === 'reference');

      // Extract package_name (if present)
      const packageName = template.name || '';
      const requestValue = referenceConfig?.package_name || null;

      // Format last_used date to "15 Oct 2025" format
      let lastUsed = this.formatDate(template.last_used);
      if(lastUsed==null){
          lastUsed = $localize`Never used`
      }

      // Get times used as string
      const timesUsed = template.usage_count != null ? String(template.usage_count) : '0';

      // Return mapped template object
      return {
        ...template,
        packageName,
        lastUsed,
        lastUsedLabel: $localize`Last used`,
        requestLabel: $localize`Survey`,
        requestValue,
        requestIcon: 'https://assets.xref.com/omni/shared/new_reference_icon.svg',
        backgroundChecks,
        timesUsed,
        checkLabel: $localize`Background checks`,
        timeLabel: $localize`Times used`,
      };
    });
  }

  private formatDate(date: string | null | undefined): string | null {
    if (!date) {
      return null;
    }
    // Format date from "YYYY-MM-DD" to "D MMM YYYY" (e.g., "22 Sept 2025")
    return moment(date, 'YYYY-MM-DD').format('D MMM YYYY');
  }

  fetchTrustMarketplaceStatus() {
    this.trustMarketplaceStatusService.trustStatus$.subscribe(
      res => {
        this.hasBackgroundCheckPermissions = res === 3;
      }, error => {
        this.alertList.append([{
          type: AlertType.DANGER,
          message: $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`,
          keepOnRouteChange: true
        }]);
      }
    );
      this.trustMarketplaceStatusService.trustAdminStatus.subscribe(
        res => {
          if(res != undefined){
            this.tmpAdminStatus = res;
          }
        }, error => {
          this.alertList.append([{
            type: AlertType.DANGER,
            message: $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`,
            keepOnRouteChange: true
          }]);
        }
      );
  }

  select(surveyType): void {
    this.selectClick.emit({ data: surveyType});
    this.bsModalRef.hide();
    this.permissions.setNavCheck(false);
  }

  cancel(): void {
    this.bsModalRef.hide();
  }

  onCardClick(template:any){
      // todo navigate to send request page
      this.selectClick.emit({ data: 'reference', template: template});
      this.bsModalRef.hide();
      this.permissions.setNavCheck(false);
  }
  createTemplate(event: any){
      this.bsModalRef.hide();
      this.permissions.setNavCheck(false);
      this.router.navigateByUrl('/template', {
        state: {
        }
      });
  }

  ngOnDestroy(): void {
      if(this.requestTemplate){
          this.destroy$.next();
          this.destroy$.complete();
      }
  }

  onPageChange(page: number): void {
    if (this.currentPage !== page) {
      this.currentPage = page;
      this.getAndProcessTemplatesList();
    }
  }

  onPageSizeChange(rows: number): void {
    this.rowsPerView = rows;
    this.currentPage = 1;
    this.getAndProcessTemplatesList();
  }

  onSearchEvent(event: any): void {
    this.searchSubject$.next(event.search);
  }

  clearSearchResult(event) {
    // Use searchSubject$ for consistency with debouncing
    this.searchSubject$.next('');
  }

}
